/**
 * Centralized governance microcopy.
 *
 * Single source of truth for all "governance perception" strings exposed in UI.
 * Rules:
 *  - Business language only — never expose technical names (views, tables, RPCs, edge functions, hashes).
 *  - Changes here propagate to the entire app, ensuring consistency and trivial rollback.
 */

export const GOVERNANCE_COPY = {
  provenance: {
    // Phase 3.5: "Fonte certificada" foi substituído por "Origem auditável" para
    // descrever a propriedade real (trilha auditável preservada) sem implicar
    // certificação por terceiros — termo mais preciso para investidores e IFs.
    label: 'Origem auditável',
    tooltip:
      'Indicador derivado de fonte única da plataforma, com versionamento e trilha de auditoria preservada para fins de governança.',
    updatedJustNow: 'atualizado agora',
    updatedSuffix: 'atualizado há',
  },
  analyticalHints: {
    indicators:
      'Indicadores consolidados a partir de fonte única, com versionamento e trilha de auditoria preservada.',
    riskIntelligence:
      'Visão derivada de fonte única auditável, com segregação de funções e versionamento aplicados a toda a carteira.',
    ifPortfolio:
      'Carteira fiduciária consolidada com origem auditável, segregação de funções e versionamento contratual preservados.',
    contractEvm:
      'Indicadores EVM derivados de fonte única, com metodologia uniforme aplicada a todos os contratos.',
    sCurve:
      'Curva de progresso derivada da base contratual vigente, com rastreabilidade preservada.',
    monteCarlo:
      'Projeção estatística derivada do histórico auditado do contrato.',
  },
  emptyStates: {
    qualityQueueClean: {
      title: 'Fila auditada · 0 pendências',
      hint: 'Nenhuma execução aguardando inspeção de qualidade no momento.',
    },
    planningQueueClean: {
      title: 'Fila auditada · 0 pendências',
      hint: 'Nenhuma execução aguardando aprovação do planejamento no momento.',
    },
    filtersNoResult: {
      title: 'Sem resultados para os filtros aplicados',
      hint: 'Ajuste os critérios para visualizar registros.',
    },
  },
  sod: {
    title: 'Segregação de funções ativa',
    descriptionFor: (perfil: string) =>
      `Ação reservada ao perfil de ${perfil}. A separação de responsabilidades é aplicada de forma estrutural pela plataforma.`,
  },
  healthScore: {
    methodologyFooter:
      'Metodologia uniforme aplicada a todos os contratos. Critérios versionados e auditáveis.',
  },
  contractHeader: {
    groupLabel: 'Governança contratual',
  },
  footer: {
    line: 'NetUno SGPI · Governança auditável',
  },
  /**
   * Subtítulo institucional padronizado para telas analíticas.
   * Uso opcional logo abaixo do título da página para reforçar contexto.
   */
  institutionalSubtitle:
    'Visão consolidada com origem auditável, segregação de funções e versionamento contratual aplicados de forma transversal.',
  /**
   * Conteúdo do bloco transversal "Governança ativa do sistema".
   * Reapresenta princípios já implementados — não introduz novas funcionalidades.
   */
  governanceBlock: {
    title: 'Governança ativa do sistema',
    footnote:
      'Princípios operacionais aplicados de forma contínua e uniforme a toda a plataforma.',
  },
  governancePrinciples: [
    {
      label: 'Origem auditável',
      description:
        'Indicadores derivados de fonte única, com trilha de auditoria preservada.',
    },
    {
      label: 'Versionamento contratual',
      description:
        'Cada alteração contratual é registrada com versão e contexto preservados.',
    },
    {
      label: 'Segregação de funções',
      description:
        'Aprovação, execução e validação ocorrem em perfis distintos e independentes.',
    },
    {
      label: 'Registros consolidados',
      description:
        'Eventos passam por controles obrigatórios antes de serem considerados consolidados.',
    },
    {
      label: 'Histórico preservado',
      description:
        'Decisões e medições aprovadas são mantidas íntegras ao longo do tempo.',
    },
    {
      label: 'Metodologia uniforme',
      description:
        'Critérios de cálculo e validação aplicados de forma idêntica a toda a carteira.',
    },
  ] as const,
  /**
   * Mensagens interpretativas para estados vazios em telas de governança.
   * Comunicam disciplina operacional em vez de "nada para mostrar".
   */
  interpretiveEmptyStates: {
    indicatorsClean:
      'Nenhum indicador pendente de consolidação no período selecionado. A ausência de itens reflete a aplicação contínua dos controles de governança.',
    riskClean:
      'Carteira sem alertas críticos ativos. Os controles de risco continuam aplicados de forma transversal a todos os contratos.',
    portfolioClean:
      'Nenhum contrato exige atenção imediata no momento. A carteira segue sob monitoramento contínuo com segregação de funções ativa.',
    contractsClean:
      'Nenhum contrato corresponde aos filtros atuais. Os critérios de governança permanecem aplicados a toda a base.',
  },
} as const;


/**
 * Maps internal role identifiers to business-friendly labels used in SoD notices.
 * Never expose the raw role key.
 */
export function rolePerfilLabel(role: string): string {
  const map: Record<string, string> = {
    gestor_qualidade: 'Qualidade',
    gestor_planejamento: 'Planejamento',
    gestor_execucao: 'Execução',
    gestor_documentacao: 'Documentação',
    apontador: 'Apontamento',
    instituicao_financeira: 'Instituição Financeira',
    admin_cluster: 'Administração de Cluster',
    admin: 'Administração',
    coadmin: 'Administração',
    observador: 'Observador',
  };
  return map[role] ?? 'perfil específico';
}

/**
 * Returns a short, business-language "updated X ago" label, or null if no date.
 */
export function formatProvenanceTimestamp(updatedAt?: Date | string | null): string | null {
  if (!updatedAt) return null;
  const d = updatedAt instanceof Date ? updatedAt : new Date(updatedAt);
  if (Number.isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  if (diffMin < 1) return GOVERNANCE_COPY.provenance.updatedJustNow;
  if (diffMin < 60) return `${GOVERNANCE_COPY.provenance.updatedSuffix} ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${GOVERNANCE_COPY.provenance.updatedSuffix} ${diffH} h`;
  const diffD = Math.floor(diffMin / 60 / 24);
  return `${GOVERNANCE_COPY.provenance.updatedSuffix} ${diffD} d`;
}
